import request from '../utils/request';

/**
 * 将录音文件发送到后端进行转录
 * @param audioBlob 录音文件的 Blob 对象
 * @param mimeType 录音文件的 MIME 类型
 * @returns 转录后的文本
 */
export const transcribeAudio = async (audioBlob: Blob, mimeType: string = 'audio/webm'): Promise<any> => {
  const formData = new FormData();
  
  // 根据 MIME 类型确定文件扩展名
  let extension = 'webm';
  if (mimeType.includes('mp4')) extension = 'mp4';
  else if (mimeType.includes('ogg')) extension = 'ogg';
  else if (mimeType.includes('wav')) extension = 'wav';
  else if (mimeType.includes('mpeg')) extension = 'mp3';
  else if (mimeType.includes('webm')) extension = 'webm';

  // 转换为 mp4 或 wav 格式，后端 whisper 比较好处理
  // 注意：文件名后缀很重要，Whisper API 根据后缀判断文件类型
  formData.append('file', audioBlob, `recording.${extension}`);

  return request.post('/speech/transcribe', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
  * 语音合成 (TTS) 类，支持后端 API（如硅基流动）
  * 优化点：支持句子分段朗读，实现低延迟
  */
 class SpeechSynthesizer {
  private audio: HTMLAudioElement;
  private onEndCallback: (() => void) | null = null;
  private sentenceQueue: string[] = [];
  private currentSentenceIndex: number = -1;
  private preloadedBlobs: Map<number, Blob> = new Map();
  private pendingRequests: Map<number, Promise<void>> = new Map();
  private isSynthesizing: boolean = false;
  private isStopped: boolean = false;
  private currentAudioUrl: string | null = null;

  constructor() {
    this.audio = new Audio();
    // 预绑定事件，防止在移动端浏览器中重复创建对象导致的手势限制
    this.audio.onended = () => this.handleAudioEnded();
    this.audio.onerror = (e) => this.handleAudioError(e);
  }

  private handleAudioEnded() {
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = null;
    }
    this.preloadedBlobs.delete(this.currentSentenceIndex);
    this.playSentence(this.currentSentenceIndex + 1);
  }

  private handleAudioError(e: any) {
    const error = this.audio.error;
    let errorMessage = 'Unknown audio error';
    if (error) {
      switch (error.code) {
        case 1: errorMessage = 'MEDIA_ERR_ABORTED - 播放被终止'; break;
        case 2: errorMessage = 'MEDIA_ERR_NETWORK - 网络错误'; break;
        case 3: errorMessage = 'MEDIA_ERR_DECODE - 解码错误'; break;
        case 4: errorMessage = 'MEDIA_ERR_SRC_NOT_SUPPORTED - 格式不支持'; break;
      }
    }
    console.error(`Audio playback error (${errorMessage}):`, e);
    
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = null;
    }
    // 发生错误时跳到下一句，防止卡死
    this.playSentence(this.currentSentenceIndex + 1);
  }

  /**
   * 清理文本中的 Markdown 标签和多余符号，使其适合朗读
   */
  private cleanText(text: string): string {
    return text
      .replace(/```[\s\S]*?```/g, ' [代码块已跳过] ') // 跳过代码块
      .replace(/`([^`]+)`/g, '$1') // 去掉内联代码的反引号
      .replace(/#+\s+(.+)/g, '$1') // 去掉标题符号
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // 去掉链接，只保留文本
      .replace(/\*\*(.+?)\*\*/g, '$1') // 去掉加粗
      .replace(/\*(.+?)\*/g, '$1') // 去掉斜体
      .replace(/!\[.*?\]\(.*?\)/g, '') // 去掉图片标签
      .replace(/>\s+(.+)/g, '$1') // 去掉引用
      .replace(/\s+/g, ' ') // 压缩空白
      .replace(/\|/g, ' ') // 去掉表格符号
      .trim();
  }

  /**
   * 将长文本切割为句子，以便实现低延迟朗读
   */
  private splitIntoSentences(text: string): string[] {
    // 按中英文标点和换行符切割
    const sentences = text.split(/([。！？\n!?;；]+)/g);
    const result: string[] = [];
    let current = '';

    for (let i = 0; i < sentences.length; i++) {
      const part = sentences[i];
      if (!part) continue;

      if (/^[。！？\n!?;；]+$/.test(part)) {
        // 如果是标点符号，附加到前一个句子
        if (current) {
          current += part;
          result.push(current.trim());
          current = '';
        }
      } else {
        // 如果是文本，记录下来
        if (current) result.push(current.trim());
        current = part;
      }
    }

    if (current) result.push(current.trim());
    
    // 过滤掉空字符串和过短的段落，并限制每段长度（防止单段过长）
    return result
      .filter(s => s.length > 0)
      .flatMap(s => {
        if (s.length > 150) {
          // 如果某句话依然非常长（例如没有标点），强制按长度切割
          const chunks = [];
          for (let i = 0; i < s.length; i += 150) {
            chunks.push(s.substring(i, i + 150));
          }
          return chunks;
        }
        return s;
      });
  }

  /**
   * 预加载下一句的音频
   */
  private async preloadNext(index: number): Promise<void> {
    if (index >= this.sentenceQueue.length || this.isStopped || this.preloadedBlobs.has(index)) {
      return;
    }

    // 如果已经有正在进行的请求，直接返回该请求的 Promise
    if (this.pendingRequests.has(index)) {
      return this.pendingRequests.get(index);
    }

    const requestPromise = (async () => {
      try {
        const response = await request.post('/speech/synthesize', {
          text: this.sentenceQueue[index]
        }, {
          responseType: 'blob'
        });
        
        // 验证并处理 Blob
        const audioBlob = await this.validateAudioBlob(response);
        
        if (!this.isStopped && audioBlob) {
           this.preloadedBlobs.set(index, audioBlob);
         }
      } catch (error) {
        console.error(`Failed to preload sentence ${index}:`, error);
      } finally {
        this.pendingRequests.delete(index);
      }
    })();

    this.pendingRequests.set(index, requestPromise);
    return requestPromise;
  }

  /**
   * 验证音频 Blob 是否有效
   */
  private async validateAudioBlob(response: any): Promise<Blob | null> {
    // request.ts 可能已经将错误 JSON 返回
    if (!(response instanceof Blob)) {
      if (response && (response.error || response.code !== undefined)) {
        console.error('TTS API returned error JSON:', response);
      }
      return null;
    }

    const blob = response;

    // 检查是否可能是错误信息（JSON 格式通常很小且不是 audio/mpeg）
    if (blob.type === 'application/json' || (blob.type === 'text/plain' && blob.size < 500) || (blob.type === '' && blob.size < 500)) {
      try {
        const text = await blob.text();
        const json = JSON.parse(text);
        console.error('TTS API returned error message in blob:', json);
        return null;
      } catch (e) {
        // 不是 JSON，如果 size 太小也可能有问题
        if (blob.size < 100) {
          console.error('TTS API returned too small blob:', blob.size, blob.type);
          return null;
        }
      }
    }

    // 记录前几个字节以辅助调试
    if (blob.size > 0) {
      const buffer = await blob.slice(0, 10).arrayBuffer();
      const view = new Uint8Array(buffer);
      const hex = Array.from(view).map(b => b.toString(16).padStart(2, '0')).join(' ');
      
      // 常见音频签名
      const isMP3 = (view[0] === 0x49 && view[1] === 0x44 && view[2] === 0x33) || (view[0] === 0xFF && (view[1] & 0xE0) === 0xE0);
      const isWAV = view[0] === 0x52 && view[1] === 0x49 && view[2] === 0x46 && view[3] === 0x46; // RIFF
      const isOGG = view[0] === 0x4f && view[1] === 0x67 && view[2] === 0x67 && view[3] === 0x53; // OggS
      const isWEBM = view[0] === 0x1a && view[1] === 0x45 && view[2] === 0xdf && view[3] === 0xa3;

      const isValidAudio = isMP3 || isWAV || isOGG || isWEBM;
      
      console.log(`Audio check - size: ${blob.size}, type: ${blob.type}, signature: ${hex}, valid: ${isValidAudio}`);

      if (!isValidAudio && blob.size > 0) {
        // 如果不是任何已知音频格式，且包含打印字符，可能是某种文本错误
        const text = await blob.slice(0, 100).text();
        if (/^[a-zA-Z0-9\s\{\}\"\'\:]+$/.test(text)) {
          console.error('Blob content appears to be text/JSON instead of audio:', text);
          return null;
        }
        
        // 如果是非常小且不符合签名的，直接舍弃
        if (blob.size < 500) {
          console.error('Invalid or too small audio blob discarded');
          return null;
        }
      }
    } else {
      console.error('Empty audio blob received');
      return null;
    }

    return blob;
  }

  /**
   * 播放指定索引的句子
   */
  private async playSentence(index: number) {
    if (index >= this.sentenceQueue.length || this.isStopped) {
      this.isSynthesizing = false;
      if (this.onEndCallback) this.onEndCallback();
      return;
    }

    this.currentSentenceIndex = index;
    
    // 如果有正在进行的请求，等待它完成
    if (this.pendingRequests.has(index)) {
      await this.pendingRequests.get(index);
    }

    let audioBlob = this.preloadedBlobs.get(index);

    try {
      if (!audioBlob) {
        // 如果还没预加载好且没有待处理请求，则现场请求
        const response = await request.post('/speech/synthesize', {
          text: this.sentenceQueue[index]
        }, {
          responseType: 'blob'
        });
        audioBlob = await this.validateAudioBlob(response) || undefined;
      }

      if (!audioBlob) {
        throw new Error('Invalid audio blob after fetch');
      }

      // 异步预加载后续三句
      for (let i = 1; i <= 3; i++) {
        this.preloadNext(index + i);
      }

      if (this.currentAudioUrl) {
        URL.revokeObjectURL(this.currentAudioUrl);
      }
      
      console.log(`Playing sentence ${index}, blob size: ${audioBlob.size}, type: ${audioBlob.type}`);
      
      this.currentAudioUrl = URL.createObjectURL(audioBlob);
      this.audio.src = this.currentAudioUrl;
      
      // 捕获播放错误
      try {
        await this.audio.play();
      } catch (playError) {
        console.error('Playback failed immediately:', playError);
        this.handleAudioError(playError);
      }
    } catch (error) {
      console.error(`Failed to play sentence ${index}:`, error);
      // 发生错误时跳到下一句
      this.playSentence(index + 1);
    }
  }

  /**
   * 播放语音
   */
  async speak(text: string, onEnd?: () => void) {
    this.stop();
    
    // 关键：在用户手势回调中立即触发一次播放（即使是空的），用于解锁移动端浏览器的音频限制
    try {
      this.audio.play().catch(() => {}); 
      this.audio.pause();
    } catch (e) {}

    const cleanedText = this.cleanText(text);
    if (!cleanedText) {
      if (onEnd) onEnd();
      return;
    }

    this.isStopped = false;
    this.onEndCallback = onEnd || null;
    this.sentenceQueue = this.splitIntoSentences(cleanedText);
    this.currentSentenceIndex = 0;
    this.preloadedBlobs.clear();
    this.isSynthesizing = true;

    if (this.sentenceQueue.length === 0) {
      if (onEnd) onEnd();
      return;
    }

    // 并行预加载前两句
    this.preloadNext(0);
    this.preloadNext(1);

    // 立即开始播放流程（playSentence 内部会处理等待请求完成）
    this.playSentence(0);
  }

  /**
   * 停止播放
   */
  stop() {
    this.isStopped = true;
    this.isSynthesizing = false;
    this.sentenceQueue = [];
    this.currentSentenceIndex = -1;
    this.preloadedBlobs.clear();
    this.pendingRequests.clear();

    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio.currentTime = 0;
    }

    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = null;
    }
  }

  /**
   * 是否正在播放
   */
  isSpeaking(): boolean {
    return this.isSynthesizing || (this.audio !== null && !this.audio.paused);
  }
}

export const speechService = new SpeechSynthesizer();
